/* eslint-disable camelcase */
/* NBS, cIndOp e cClassTrib do IBS/CBS: toda nota leva os três.
   Serviço no Anexo VIII: o NBS traz os cIndOp e cClassTrib aceitos e a tela preenche a
   primeira opção. Serviço fora do anexo: NBS digitado, cIndOp e cClassTrib de todas as
   opções (padrão 100301 / 000001). Adquirente no exterior (tomador lá fora ou exportação)
   troca o cIndOp pela variante dele; exportação é imune (410027). */
import React, { useEffect, useMemo } from 'react';
import { SimpleGrid } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { InputForm } from '../../../../../../components/inputs/forms/InputForm';
import { useNfseEmissao } from '../../../../../../hooks/useNfseEmissao';
import { INfseCodigoIbsCbs } from '../../../../../../types/nfseEmissao';
import { CampoLista } from './ComexSection';

const digitos = (v?: string | null) => (v ?? '').replace(/\D/g, '');

const IND_OP_PADRAO = '100301';
const CLASS_TRIB_PADRAO = '000001';
const EXPORTACAO: INfseCodigoIbsCbs = {
  codigo: '410027',
  descricao: 'Exportação de serviço (imune, LC 214/2025 art. 8º)',
};

export const formatarNbs = (n: string) =>
  n.length === 9 ? `${n[0]}.${n.slice(1, 5)}.${n.slice(5, 7)}.${n.slice(7)}` : n;

const lista = (codigos: INfseCodigoIbsCbs[]) =>
  codigos.map(c => ({ value: c.codigo, label: `${c.codigo} · ${c.descricao}` }));

export function IbsCbsCampos({ isDisabled }: { isDisabled?: boolean }) {
  const { getIbsCbs } = useNfseEmissao();
  const { getValues, setValue } = useFormContext();
  const cTribNac = digitos(useWatch({ name: 'c_trib_nac' }) as string);
  const nbs = digitos(useWatch({ name: 'c_nbs' }) as string);
  const classe = digitos(useWatch({ name: 'c_class_trib' }) as string);
  const exportacao = useWatch({ name: 'trib_issqn' }) === '3';
  const tomadorExterior = !!useWatch({ name: 'tomador_exterior' });
  const noExterior = exportacao || tomadorExterior;

  const consulta = useQuery({
    queryKey: ['nfse-ibscbs', cTribNac],
    queryFn: () => getIbsCbs(cTribNac),
    enabled: cTribNac.length === 6,
    staleTime: Infinity,
  });
  const dados = consulta.data;
  const opcoes = dados?.opcoes ?? [];
  const atual = opcoes.find(o => o.nbs === nbs);
  // Serviço com vários NBS e nenhum escolhido: cIndOp e cClassTrib esperam o NBS.
  const esperaNbs = opcoes.length > 0 && !atual;

  const { indOps, classes, indPadrao, classePadrao } = useMemo(() => {
    const todosInd = dados?.ind_op_todos ?? [];
    const nome = (codigo: string) => todosInd.find(i => i.codigo === codigo)?.descricao ?? codigo;
    const base = atual?.ind_op.length ? atual.ind_op : todosInd;
    const mapa = noExterior ? (dados?.ind_op_exterior ?? {}) : {};
    const codigos = Array.from(new Set(base.map(i => mapa[i.codigo] ?? i.codigo)));
    const inds = codigos.map(c => ({ codigo: c, descricao: nome(c) }));
    const padraoInd = mapa[IND_OP_PADRAO] ?? IND_OP_PADRAO;
    let cls: INfseCodigoIbsCbs[] = atual ? atual.class_trib : (dados?.class_trib_todos ?? []);
    if (exportacao) cls = [EXPORTACAO];
    return {
      indOps: esperaNbs ? [] : inds,
      classes: esperaNbs ? [] : cls,
      // Primeira do NBS (vazio se o NBS não tem cIndOp válido: exige escolha); fora da
      // tabela, o padrão.
      indPadrao: atual ? (atual.ind_op.length && inds[0]?.codigo) || '' : padraoInd,
      classePadrao: exportacao
        ? EXPORTACAO.codigo
        : (atual?.class_trib[0]?.codigo ?? CLASS_TRIB_PADRAO),
    };
  }, [dados, atual, esperaNbs, noExterior, exportacao]);

  // NBS vazio ganha o padrão do serviço; NBS com pontos de rascunho antigo vira só dígitos.
  useEffect(() => {
    if (!dados) return;
    const bruto = (getValues('c_nbs') as string) ?? '';
    if (!digitos(bruto) && dados.nbs_padrao) setValue('c_nbs', dados.nbs_padrao);
    else if (bruto !== digitos(bruto)) setValue('c_nbs', digitos(bruto));
  }, [dados, getValues, setValue]);

  // Valor que não vale para o NBS / situação atual volta para o padrão.
  useEffect(() => {
    if (!dados || esperaNbs) return;
    const ajustar = (campo: string, aceitos: INfseCodigoIbsCbs[], padrao: string) => {
      const valor = (getValues(campo) as string) ?? '';
      if (!aceitos.some(a => a.codigo === valor)) setValue(campo, padrao, { shouldDirty: true });
    };
    ajustar('c_ind_op', indOps, indPadrao);
    ajustar('c_class_trib', classes, classePadrao);
  }, [dados, esperaNbs, indOps, classes, indPadrao, classePadrao, getValues, setValue]);

  const semIndOpNaTabela = !!atual && !atual.ind_op.length;
  let indPlaceholder: string | undefined;
  if (esperaNbs) indPlaceholder = 'Escolha o NBS';
  else if (semIndOpNaTabela) indPlaceholder = 'Selecione';
  let indAjuda: string | undefined;
  if (semIndOpNaTabela) indAjuda = 'O Anexo VIII não traz um código válido para este NBS: escolha';
  else if (noExterior) indAjuda = 'Adquirente no exterior: local é o domicílio do destinatário';

  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} columnGap={4}>
      {opcoes.length ? (
        <CampoLista
          name="c_nbs"
          label="NBS do serviço"
          opcoes={opcoes.map(o => ({
            value: o.nbs,
            label: `${formatarNbs(o.nbs)} · ${o.descricao}`,
          }))}
          placeholder={opcoes.length > 1 ? 'Selecione' : undefined}
          isRequired
          isDisabled={isDisabled}
          ajuda={opcoes.length > 1 ? `${opcoes.length} NBS possíveis no Anexo VIII` : undefined}
        />
      ) : (
        <InputForm
          name="c_nbs"
          label="Código NBS"
          placeholder="9 dígitos"
          isRequired
          isDisabled={isDisabled}
          helper={
            cTribNac.length === 6 && consulta.isSuccess
              ? 'Serviço fora do Anexo VIII: informe o NBS'
              : undefined
          }
        />
      )}
      <CampoLista
        name="c_ind_op"
        label="Indicador da operação (cIndOp)"
        opcoes={lista(indOps)}
        placeholder={indPlaceholder}
        isRequired
        isDisabled={isDisabled || esperaNbs || !dados}
        ajuda={indAjuda}
      />
      <CampoLista
        name="c_class_trib"
        label="Classificação (cClassTrib)"
        opcoes={lista(classes)}
        placeholder={esperaNbs ? 'Escolha o NBS' : undefined}
        isRequired
        isDisabled={isDisabled || esperaNbs || !dados}
        ajuda={classe ? `CST ${classe.slice(0, 3)}` : undefined}
      />
    </SimpleGrid>
  );
}
